import { v as validateCustomKey, c as containsHtml, a as validatePlaceholderName, p as placeholdersMatch, e as extractPlaceholders, b as customLabelId, L as LocalizationRequestError } from './shared/clientErrors-COds4E9J.mjs';
export { C as CUSTOM_KEY_MAX_LENGTH, d as CUSTOM_PREFIX, f as LANGUAGE_UNAVAILABLE_ERROR_CODES, g as LocalizationCapabilityError, h as cascadeKeysForEntity, i as cascadeKeysForUi, j as createCatalogTranslator, k as createTranslator, l as interpolate, m as isLanguageUnavailableError, n as isStorableLabelIdLength, o as layersFromProjectTranslations, r as resolve, q as resolveLabel, s as resolveLayered, t as stripSurface, w as withSurface } from './shared/clientErrors-COds4E9J.mjs';
export { MAX_LABEL_ID_BASE_LENGTH, SURFACES, SURFACE_SEPARATOR, isSurfaceName } from '@canvas-logic/clp-api-types';

const labelId = {
    productTypeName: (step, ptKey) => `${step}:${ptKey}.name`,
    productTypeDesc: (step, ptKey) => `${step}:${ptKey}.desc`,
    productName: (step, ptKey, productKey) => `${step}:${ptKey}.prod:${productKey}.name`,
    productDesc: (step, ptKey, productKey) => `${step}:${ptKey}.prod:${productKey}.desc`,
    attributeName: (step, ptKey, attrKey) => `${step}:${ptKey}.attr:${attrKey}.name`,
    attributeValue: (step, ptKey, attrKey, valueKey) => `${step}:${ptKey}.attr:${attrKey}.val:${valueKey}`,
    slotTitle: (cptKey, cmpKey) => `cpt:${cptKey}.cmp:${cmpKey}.slot.title`,
    slotDesc: (cptKey, cmpKey) => `cpt:${cptKey}.cmp:${cmpKey}.slot.desc`,
    uiCfgGroupTitle: (cptKey, groupId) => `cpt:${cptKey}.uicfg.group:${groupId}.title`,
    uiCfgGroupDesc: (cptKey, groupId) => `cpt:${cptKey}.uicfg.group:${groupId}.desc`,
    priceTypeName: (catalogKey, ptKey) => `cat:${catalogKey}.price:${ptKey}.name`,
};

function buildSlotDisplayContext(parentDisplayCtx, state) {
    var _a, _b, _c, _d, _e;
    if (!(state === null || state === void 0 ? void 0 : state.componentKey)) {
        return parentDisplayCtx;
    }
    const filler = (_b = (_a = state.api) === null || _a === void 0 ? void 0 : _a.getComponentProductType) === null || _b === void 0 ? void 0 : _b.call(_a, state.componentKey);
    const next = Object.assign(Object.assign({}, parentDisplayCtx), { cmpKey: state.componentKey });
    if (!filler) {
        return next;
    }
    if (filler.isComposite) {
        next.cptKey = filler.key;
        const nestedProductKey = (_e = (_d = (_c = state.api) === null || _c === void 0 ? void 0 : _c.getComponentProductData) === null || _d === void 0 ? void 0 : _d.call(_c, state.componentKey)) === null || _e === void 0 ? void 0 : _e.productKey;
        if (nestedProductKey)
            next.cprodKey = nestedProductKey;
        else
            delete next.cprodKey;
        delete next.bptKey;
        delete next.bprodKey;
        return next;
    }
    next.bptKey = filler.key;
    delete next.bprodKey;
    return next;
}

function defineMessages(catalog) {
    return catalog;
}

class CatalogMergeError extends Error {
    constructor(duplicateKeys) {
        super(`Duplicate custom keys across catalogs: ${duplicateKeys.join(', ')}`);
        this.duplicateKeys = duplicateKeys;
        this.name = 'CatalogMergeError';
    }
}
function mergeCatalogs(...catalogs) {
    const merged = {};
    const duplicates = [];
    for (const catalog of catalogs) {
        for (const [key, def] of Object.entries(catalog)) {
            if (key in merged)
                duplicates.push(key);
            else
                merged[key] = def;
        }
    }
    if (duplicates.length > 0)
        throw new CatalogMergeError(duplicates);
    return merged;
}

function validateCatalog(catalog, options) {
    const issues = [];
    for (const [key, def] of Object.entries(catalog)) {
        if (!validateCustomKey(key)) {
            issues.push({ key, code: 'KEY_INVALID', message: `Invalid custom key "${key}"` });
        }
        if (def.systemValue.trim().length === 0) {
            issues.push({ key, code: 'SYSTEM_VALUE_EMPTY', message: `System value for "${key}" is empty` });
        }
        else if (containsHtml(def.systemValue)) {
            issues.push({ key, code: 'VALUE_HTML', message: `System value for "${key}" contains HTML` });
        }
        let expected;
        let hasInvalidPlaceholderName = false;
        if (def.placeholders) {
            expected = def.placeholders;
            for (const name of def.placeholders) {
                if (!validatePlaceholderName(name)) {
                    hasInvalidPlaceholderName = true;
                    issues.push({
                        key,
                        code: 'PLACEHOLDER_NAME_INVALID',
                        message: `Invalid placeholder name "${name}" on "${key}"`,
                    });
                }
            }
            if (!hasInvalidPlaceholderName &&
                def.systemValue.trim().length > 0 &&
                !placeholdersMatch(def.systemValue, expected)) {
                issues.push({
                    key,
                    code: 'PLACEHOLDER_MISMATCH',
                    message: `System value of "${key}" does not contain exactly the declared placeholders`,
                });
            }
        }
        else {
            expected = extractPlaceholders(def.systemValue);
        }
        for (const language of options.languages) {
            const value = def.translations[language];
            if (value == null || value.trim().length === 0) {
                issues.push({ key, code: 'TRANSLATION_MISSING', language, message: `"${key}" has no ${language} value` });
                continue;
            }
            if (containsHtml(value)) {
                issues.push({ key, code: 'VALUE_HTML', language, message: `${language} value of "${key}" contains HTML` });
                continue;
            }
            if (!hasInvalidPlaceholderName && !placeholdersMatch(value, expected)) {
                issues.push({
                    key,
                    code: 'PLACEHOLDER_MISMATCH',
                    language,
                    message: `${language} value of "${key}" does not contain exactly the placeholders [${expected.join(', ')}]`,
                });
            }
        }
    }
    return issues;
}

const EXCEL_CSV_DELIMITER = '\t';
const EXCEL_CSV_CONTENT_TYPE = 'text/csv; charset=utf-16le';
function escapeCsvCell(value, delimiter = EXCEL_CSV_DELIMITER) {
    if (value == null)
        return '';
    if (value.includes('"') || value.includes(delimiter) || value.includes('\n') || value.includes('\r')) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}
function buildCsvLine(cells, delimiter = EXCEL_CSV_DELIMITER) {
    return cells.map((cell) => escapeCsvCell(cell, delimiter)).join(delimiter);
}
function encodeExcelCsv(text) {
    const bytes = new Uint8Array(2 + text.length * 2);
    bytes[0] = 0xff;
    bytes[1] = 0xfe;
    for (let i = 0; i < text.length; i++) {
        const code = text.charCodeAt(i);
        bytes[2 + i * 2] = code & 0xff;
        bytes[3 + i * 2] = code >>> 8;
    }
    return bytes;
}

const KEYS_CSV_HEADERS = ['key', 'systemValue', 'placeholders'];
const TRANSLATIONS_CSV_COLUMNS = { LabelId: 'Label_ID', Type: 'Type', SystemValue: 'System_Value' };
const CUSTOM_LABEL_TYPE = 'custom';
function buildCustomTranslationCsvs(catalog, options) {
    const issues = validateCatalog(catalog, { languages: options.languages });
    if (issues.length > 0)
        return { ok: false, issues };
    const sortedKeys = Object.keys(catalog).sort();
    const keysLines = [buildCsvLine([...KEYS_CSV_HEADERS])];
    for (const key of sortedKeys) {
        const def = catalog[key];
        const placeholders = def.placeholders ? [...def.placeholders].sort() : extractPlaceholders(def.systemValue);
        keysLines.push(buildCsvLine([key, def.systemValue, placeholders.join(' ')]));
    }
    const translationsHeader = [
        TRANSLATIONS_CSV_COLUMNS.LabelId,
        TRANSLATIONS_CSV_COLUMNS.Type,
        TRANSLATIONS_CSV_COLUMNS.SystemValue,
        ...options.languages.map((code) => code.toUpperCase()),
    ];
    const translationsLines = [buildCsvLine(translationsHeader)];
    for (const key of sortedKeys) {
        const def = catalog[key];
        const cells = [customLabelId(key), CUSTOM_LABEL_TYPE, def.systemValue];
        for (const code of options.languages) {
            cells.push(def.translations[code]);
        }
        translationsLines.push(buildCsvLine(cells));
    }
    return {
        ok: true,
        translationKeysCsv: keysLines.join('\n') + '\n',
        translationsCsv: translationsLines.join('\n') + '\n',
    };
}

const FAMILIES = ['custom', 'entities', 'ui'];
const LAYERS = ['default', 'selected', 'system'];
function normalizeFamilies(input) {
    return normalizeAxis(input, FAMILIES, 'include');
}
function normalizeLayers(input) {
    return normalizeAxis(input, LAYERS, 'layers');
}
function normalizeAxis(input, valid, axis) {
    const tokens = Array.from(new Set(input.map((t) => t.trim().toLowerCase()))).sort();
    const unknown = tokens.filter((t) => !valid.includes(t));
    if (unknown.length > 0) {
        throw new LocalizationRequestError(`Unknown ${axis} token(s): ${unknown.map((t) => `"${t}"`).join(', ')}. Valid: ${valid.join(', ')}`);
    }
    return tokens;
}

export { CatalogMergeError, EXCEL_CSV_CONTENT_TYPE, EXCEL_CSV_DELIMITER, LocalizationRequestError, buildCsvLine, buildCustomTranslationCsvs, buildSlotDisplayContext, containsHtml, customLabelId, defineMessages, encodeExcelCsv, escapeCsvCell, extractPlaceholders, labelId, mergeCatalogs, normalizeFamilies, normalizeLayers, placeholdersMatch, validateCatalog, validateCustomKey, validatePlaceholderName };
//# sourceMappingURL=bundle.mjs.map
